import classNames from 'classnames';
import { Fragment, useMemo } from 'react';

import { ProbationReviewStatusBadge } from '@/common/components/employees/probation-review';
import { EmployeeLink } from '@/common/components/shared/link';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { NoResult } from '@/common/components/shared/no-result';
import { EMPTY_DEFAULT, Table, type TableLayout } from '@/common/components/shared/table';
import { ProbationReviewFlag, ProbationReviewReviewStatus } from '@/common/graphql';
import { EmployeeEditTab } from '@/common/types';
import { DATETIME_FORMATS, DateUtils } from '@/common/utils';

import type { DashboardProbationReview } from '../../types';

export interface ProbationReviewTableProps {
  data: Array<DashboardProbationReview>;
  loading: boolean;
  mode: 'self' | 'reviewer';
}

export const ProbationReviewTable = ({ data, loading, mode }: ProbationReviewTableProps) => {
  const layout: Array<TableLayout<DashboardProbationReview>> = useMemo(
    () =>
      [
        ...(mode === 'reviewer'
          ? ([
              {
                headerRender: () => 'Name',
                accessor: 'name',
                truncated: false,
                cellRender: ({ employeeId, employee, flags }) => (
                  <EmployeeLink
                    className={classNames('text-nowrap', {
                      'font-semibold': flags.includes(ProbationReviewFlag.Overdue),
                    })}
                    employeeId={employeeId}
                    tab={EmployeeEditTab.PROBATION_REVIEW}
                  >
                    {employee.generalInformation.preferredFullName}
                  </EmployeeLink>
                ),
              },
            ] as Array<TableLayout<DashboardProbationReview>>)
          : []),
        {
          headerRender: () => 'Planned Review Date',
          accessor: 'reviewDates',
          tdClassName: 'py-2 tabular-nums',
          truncated: false,
          cellRender: ({ reviews }) => {
            if (!reviews?.length) {
              return EMPTY_DEFAULT;
            }

            return (
              <div className="grid grid-cols-[auto_auto_20px_1fr] text-nowrap items-center">
                {reviews.map(({ deadline, reviewStatus }, index) => {
                  // Done = the reviewer submitted it, or HR completed it.
                  const done = reviewStatus !== ProbationReviewReviewStatus.Draft;

                  return (
                    <Fragment key={index}>
                      <span className={classNames('text-right', { 'text-gray-7': done })}>
                        {DateUtils.formatDate(deadline, 'MMM dd')}
                      </span>
                      <span className={classNames({ 'text-gray-7': done })}>
                        ,&nbsp;
                        {DateUtils.formatDate(deadline, 'yyyy')}
                      </span>
                      <span className="size-4 ml-1">
                        {done && <MaterialIcons iconName="check_circle" fontSize="16px" className="text-success" />}
                      </span>
                      <span />
                    </Fragment>
                  );
                })}
              </div>
            );
          },
        },
        {
          headerRender: () => 'Status',
          accessor: 'probationStatus',
          cellRender: ({ probationStatus, outcome, flags }) => (
            <ProbationReviewStatusBadge flags={flags} outcome={outcome} probationStatus={probationStatus} />
          ),
        },
        {
          headerRender: () => 'Probation End Date',
          accessor: 'probationEndDate',
          tdClassName: 'tabular-nums',
          cellRender: ({ probationEndDate, flags }) => (
            <span className={classNames({ 'font-semibold': flags.includes(ProbationReviewFlag.Overdue) })}>
              {DateUtils.formatDate(probationEndDate, DATETIME_FORMATS.MMMddyyyy)}
            </span>
          ),
        },
      ] as Array<TableLayout<DashboardProbationReview>>,
    [mode],
  );

  return (
    <Table<DashboardProbationReview>
      allowFloatingHeader={true}
      data={data}
      headerClassName="h-auto [&>th]:py-2"
      hoverable={false}
      layout={layout}
      loading={loading}
      notFoundTemplate={
        <NoResult title="No data available" titleClassName="text-sm text-gray-9" wrapperClassName="py-10" />
      }
      numOfStickyCols={0}
      page={1}
      scrollContainerClassName="max-h-[250px]"
      skeletonCount={3}
      wrapperClassName="rounded border-x border-b overflow-auto"
    />
  );
};
